import { useEffect, useState, use } from 'react';
import { toast } from 'sonner';
import { trpc } from '@/app/_trpc/client';
import { useIsMobile } from '@/hooks/useIsMobile';
import { pushAnalyticsEvent } from '@/lib/analyticsClientSide';
import { logger } from '@/lib/clientSideLogger';
import { isIOS, isStandaloneMode } from '@/lib/device';
import { DesktopPushNotificationDialogContext } from '../context/DesktopPushNotificationDialogContext';
import { IosNotificationInstructionsDialogContext } from '../context/IosNotificationInstructionsDialogContext';

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

type SubscriptionResult = {
  success: boolean;
  reason?: 'ios-limitation' | 'permission-denied' | 'error' | 'desktop-dialog';
};

export function usePushSubscription() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [registration, setRegistration] =
    useState<ServiceWorkerRegistration | null>(null);
  const [pushPermission, setPushPermission] =
    useState<NotificationPermission>('default');
  const [showDeniedDialog, setShowDeniedDialog] = useState(false);
  const { showIosPopup } = use(IosNotificationInstructionsDialogContext);
  const subscribeMutation = trpc.push.subscribe.useMutation();
  const { showDesktopDialog } = use(DesktopPushNotificationDialogContext);
  const [isMobile] = useIsMobile();

  useEffect(() => {
    if ('Notification' in window) {
      setPushPermission(Notification.permission);
    }
  }, []);

  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true);
      navigator.serviceWorker
        .register('/sw.js', {
          scope: '/',
          updateViaCache: 'none',
        })
        .then((reg) => {
          setRegistration(reg);
        })
        .catch((error) => {
          logger(
            'error',
            error instanceof Error ? error : new Error(String(error))
          );
          toast.error('Failed to register service worker');
        });
    }
  }, []);

  const subscribeToPush = async (): Promise<SubscriptionResult> => {
    if (!isMobile) {
      showDesktopDialog();
      pushAnalyticsEvent('notification - desktopDialogShown');
      return { success: false, reason: 'desktop-dialog' };
    }
    // iOS + not standalone: show popup instead of subscribing
    if (isIOS() && !isStandaloneMode()) {
      showIosPopup();
      pushAnalyticsEvent('notification - iosLimitation');
      return { success: false, reason: 'ios-limitation' };
    }

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      toast.error('Push notifications are not supported in your browser');
      return { success: false, reason: 'error' };
    }

    try {
      setIsSubscribing(true);
      const currentRegistration =
        registration || (await navigator.serviceWorker.ready);
      const sub = await currentRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY!
        ),
      });

      const serializedSub = JSON.parse(JSON.stringify(sub));
      await subscribeMutation.mutateAsync({
        subscription: serializedSub,
      });

      setPushPermission('granted');
      toast.success('Successfully subscribed to push notifications');
      pushAnalyticsEvent('notification - subscribedSuccess');
      return { success: true };
    } catch (error) {
      pushAnalyticsEvent('notification - subscribedFailed');
      if (Notification.permission === 'denied') {
        setShowDeniedDialog(true);
        return { success: false, reason: 'permission-denied' };
      }

      toast.error(
        'Failed to subscribe to push notifications. Please try again.'
      );

      if (error instanceof Error) {
        logger('error', error);
      } else {
        logger('error', new Error(String(error)));
      }

      return { success: false, reason: 'error' };
    } finally {
      setIsSubscribing(false);
    }
  };

  const updatePushSubscription = async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        return;
      }

      const currentRegistration =
        registration || (await navigator.serviceWorker.ready);
      const subscription =
        await currentRegistration.pushManager.getSubscription();

      if (!subscription) return;

      const subscriptionJson = subscription.toJSON();
      if (
        !subscriptionJson.endpoint ||
        !subscriptionJson.keys?.p256dh ||
        !subscriptionJson.keys?.auth
      ) {
        return;
      }
      const serializedSub = JSON.parse(JSON.stringify(subscriptionJson));

      await subscribeMutation.mutateAsync({
        subscription: serializedSub,
      });
    } catch (error) {
      if (error instanceof Error) {
        logger('error', error);
      } else {
        logger('error', new Error(String(error)));
      }
    }
  };

  return {
    subscribeToPush,
    isSupported,
    isSubscribing,
    updatePushSubscription,
    pushPermission,
    showDeniedDialog,
    setShowDeniedDialog,
  };
}
